// Server-side trip API client.
//
// All trip persistence goes through the Express server (server/index.js),
// which uses the Firebase Admin SDK — the browser never writes trips to
// Firestore directly (client security rules deny it; see docs/state-model.md).
// Every call sends the user's Firebase ID token as a Bearer header.
//
// Staging note: when the app is served from /test/, utils/stagingFetch.js
// rewrites these /api/tp/ paths to /api/tp-test/ (isolated trips_test
// collection) — do not hardcode absolute URLs here.
//
// customPlan contract: sent/received as a JSON string of string[][] (idea ids
// per day). The server stores it as a Firestore map and decodes it back to a
// JSON string in every response, including the public share endpoint.

/** Resolve the Firebase ID token for an authed request, or null when logged out. */
export async function getToken(user) {
  return user ? user.getIdToken() : null;
}

async function authedHeaders(user, json = false) {
  const token = await getToken(user);
  return json
    ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    : { Authorization: `Bearer ${token}` };
}

/** Create a trip document; resolves to the new trip id. */
export async function apiCreateTrip(user, data) {
  const r = await fetch('/api/tp/trips', {
    method: 'POST',
    headers: await authedHeaders(user, true),
    body: JSON.stringify(data),
  });
  const j = await r.json();
  if (!r.ok) throw new Error(j.error || 'create failed');
  return j.id;
}

/**
 * PATCH partial updates onto a trip. Protected fields (userId, shareToken,
 * createdAt) are stripped server-side. Serialize nested arrays (customPlan)
 * to JSON strings before calling.
 */
export async function apiUpdateTrip(user, id, updates) {
  const r = await fetch(`/api/tp/trips/${id}`, {
    method: 'PATCH',
    headers: await authedHeaders(user, true),
    body: JSON.stringify(updates),
  });
  if (!r.ok) { const j = await r.json(); throw new Error(j.error || 'update failed'); }
}

/** Delete a trip owned by the current user. */
export async function apiDeleteTrip(user, id) {
  const r = await fetch(`/api/tp/trips/${id}`, {
    method: 'DELETE',
    headers: await authedHeaders(user),
  });
  if (!r.ok) { const j = await r.json(); throw new Error(j.error || 'delete failed'); }
}

/** Fetch all trips for the current user, newest first. */
export async function apiFetchTrips(user) {
  const r = await fetch('/api/tp/trips', { headers: await authedHeaders(user) });
  if (!r.ok) throw new Error('fetch failed');
  const j = await r.json();
  return j.trips || [];
}

/** Fetch the current user's profile (world-map visited countries). */
export async function apiFetchProfile(user) {
  const r = await fetch('/api/tp/profile', { headers: await authedHeaders(user) });
  if (!r.ok) throw new Error('profile fetch failed');
  const j = await r.json();
  return j.profile || { visitedCountries: [] };
}

/** Replace profile fields, e.g. { visitedCountries: ['FR','JP'] }. */
export async function apiUpdateProfile(user, updates) {
  const r = await fetch('/api/tp/profile', {
    method: 'PUT',
    headers: await authedHeaders(user, true),
    body: JSON.stringify(updates),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || 'profile update failed');
  }
}
